import { createTheme } from '@mui/material/styles';

import breakpoints from 'utils/breakpoints';
import { getColor } from 'utils/colors';
import mixins from 'utils/mixins';
import { remCalc } from 'utils/remCalc';

const theme = createTheme({
	breakpoints: breakpoints,
	mixins: mixins,
});

export const styles = {};

export const overrides = {
	'.cardSlider': {
		overflow: 'hidden',
		paddingTop: remCalc(45),
		paddingBottom: remCalc(45),
		[theme.breakpoints.up('sm')]: {
			paddingTop: remCalc(80),
			paddingBottom: remCalc(80),
		},

		'.cardSlider__header': {
			marginBottom: remCalc(20),
			alignItems: 'center',
		},

		'.cardSlider__title': {
			color: getColor('ivory', 700),
			margin: 0,
			textTransform: 'uppercase',
			fontSize: remCalc(40),
			letterSpacing: remCalc(.5),
			display: 'flex',
			flexDirection: 'column',
			gap: remCalc(10),
			'.cardSlider__subtitle': {
				textTransform: 'none',
				fontWeight: 500,
				marginBottom: remCalc(18),
			},
			'.cardSlider__cta': {
				fontFamily: 'var(--font-brandon-grotesque)',
				fontSize: remCalc(16),
				textTransform: 'uppercase',
				display: 'flex',
				flexDirection: 'row',
				lineHeight: '1',
				textDecoration: 'none',
				color: getColor('ivory', 700),
				fontWeight: 400,
				'.cardSlider__cta_icon': {
					display: 'inline-flex',
					alignItems: 'center',
					justifyContent: 'center',
					'> svg': {
						width: remCalc(10),
						marginLeft: remCalc(20),
					},
				},
				'&:hover': {
					color: getColor('ivory', 600),
					textDecoration: 'none',
				},
				'&:focus-visible': {
					color: getColor('ivory', 600),
					textDecoration: 'none',
				},
			}
		},

		'.cardSlider__swiper': {
			overflow: 'visible',
			paddingBottom: remCalc(22),

			'.swiper-pagination': {
				bottom: 0,
				display: 'none',
				gap: remCalc(8),
				justifyContent: 'center',
				alignItems: 'center',
			},

			'.swiper-pagination-bullet': {
				margin: 0,
				width: remCalc(7),
				height: remCalc(7),
				borderRadius: '999px',
				background: getColor('ivory', 500),
				opacity: 1,
			},

			'.swiper-pagination-bullet-active': {
				background: getColor('ivory', 700),
			},

			[theme.breakpoints.down('sm')]: {
				'.swiper-pagination': {
					display: 'flex',
				},
			},
		},

		'.cardSlider__slide': {
			height: 'auto',
		},

		'.cardSlider__card': {
			position: 'relative',
			height: remCalc(309),
			borderRadius: 0,
			overflow: 'hidden',
			boxShadow: 'none',
			backgroundColor: getColor('ivory', 800),
		},

		'.cardSlider__media': {
			position: 'absolute',
			inset: 0,
			backgroundRepeat: 'no-repeat',
			backgroundSize: 'cover',
			backgroundPosition: 'center',
			transition: 'transform .35s ease',
		},

		'.cardSlider__overlay': {
			position: 'absolute',
			inset: 0,
			background: 'rgba(0, 0, 0, 0.3)',
			transition: 'background-color .2s',
		},

		'.cardSlider__actionArea': {
			position: 'relative',
			zIndex: 1,
			height: '100%',
			display: 'flex',
			alignItems: 'flex-end',
			textDecoration: 'none',
			color: getColor('white'),

			'&:hover': {
				textDecoration: 'none',
			},

			'&:focus-visible': {
				outline: `${remCalc(2)} solid ${getColor('blue', 400)}`,
				outlineOffset: `-${remCalc(2)}`,
			},
		},

		'.cardSlider__content': {
			width: '100%',
			display: 'flex',
			flexDirection: 'column',
			justifyContent: 'flex-end',
			gap: remCalc(20),
			padding: remCalc(48),
			height: '100%',
			transition: 'gap .35s ease',
		},

		'.cardSlider__cardSubtitle': {
			color: getColor('white'),
			margin: 0,
			maxHeight: remCalc(32),
			overflow: 'hidden',
			opacity: 1,
			transform: 'translateY(0)',
			letterSpacing: remCalc(-0.25),
			textTransform: 'uppercase',
			transition: 'opacity var(--transition-duration) ease, transform var(--transition-duration) ease, max-height var(--transition-duration) ease',
		},

		'.cardSlider__cardTitle': {
			color: getColor('white'),
			margin: 0,
			letterSpacing: remCalc(0.5),
			textTransform: 'capitalize',
			transition: 'font-size var(--transition-duration) ease, line-height var(--transition-duration) ease, letter-spacing var(--transition-duration) ease',
		},

		'.cardSlider__cardTitle--variant2': {
			fontSize: remCalc(32),
			lineHeight: remCalc(54),
			letterSpacing: remCalc(0.5),
		},

		'.cardSlider__card--variant2': {
			'.cardSlider__media': {
				transform: 'scale(1.22)',
				transformOrigin: 'center',
			},
			'.cardSlider__overlay': {
				background: 'rgba(0, 0, 0, 0.2)',
			},
			'.cardSlider__content': {
				gap: 0,
			},
		},

		'.cardSlider__card--default': {
			'&:focus-within': {
				'.cardSlider__media': {
					transform: 'scale(1.22)',
				},
				'.cardSlider__overlay': {
					background: 'rgba(0, 0, 0, 0.2)',
				},
				'.cardSlider__content': {
					gap: 0,
				},
				'.cardSlider__cardSubtitle': {
					opacity: 0,
					maxHeight: 0,
					transform: 'translateY(-8px)',
				},
				'.cardSlider__cardTitle': {
					fontSize: remCalc(49),
					lineHeight: remCalc(54),
					letterSpacing: remCalc(0.5),
				},
			},
			'@media (hover: hover) and (pointer: fine)': {
				'&:hover': {
					'.cardSlider__media': {
						transform: 'scale(1.22)',
					},
					'.cardSlider__overlay': {
						background: 'rgba(0, 0, 0, 0.2)',
					},
					'.cardSlider__content': {
						gap: 0,
					},
					'.cardSlider__cardSubtitle': {
						opacity: 0,
						maxHeight: 0,
						transform: 'translateY(-8px)',
					},
					'.cardSlider__cardTitle': {
						fontSize: remCalc(49),
						lineHeight: remCalc(54),
						letterSpacing: remCalc(0.5),
					},
				},
			},
		},

		'.cardSlider__card--event': {
			backgroundColor: getColor('white', 400),
			'.cardSlider__content': {
				color: getColor('ivory', 700),
			},
			'.cardSlider__cardSubtitle': {
				color: getColor('ivory', 700),
			},
			'.cardSlider__cardTitle': {
				color: getColor('ivory', 700),
				fontSize: remCalc(32),
				lineHeight: remCalc(40),
			},
			'.cardSlider__cardIconRow': {
				display: 'flex',
				flexDirection: 'row',
				gap: remCalc(10),
				alignItems: 'center',
				transition: 'opacity var(--transition-duration) ease, transform var(--transition-duration) ease, max-height var(--transition-duration) ease',
			},
			'.cardSlider__cardIcon': {
				width: remCalc(14),
				'&.locationPin': {
					width: 'unset',
					height: remCalc(16),
					flex: 'auto 0 0'
				}
			},
			'.cardSlider__overlay': {
				display: 'none',
			},
			'&:focus-within': {
				backgroundColor: getColor('ivory', 400),
				'.cardSlider__media': {
					transform: 'scale(1.22)',
				},
				'.cardSlider__overlay': {
					background: 'rgba(0, 0, 0, 0.2)',
				},
				'.cardSlider__content': {
					gap: 0,
				},
				'.cardSlider__cardSubtitle': {
					opacity: 0,
					maxHeight: 0,
					transform: 'translateY(-8px)',
				},
				'.cardSlider__cardTitle': {
					fontSize: remCalc(39),
					lineHeight: remCalc(47),
					letterSpacing: remCalc(0.5),
				},
				'.cardSlider__cardIconRow': {
					opacity: 0,
					maxHeight: 0,
					transform: 'translateY(-8px)',
				},
			},
			'@media (hover: hover) and (pointer: fine)': {
				'&:hover': {
					backgroundColor: getColor('ivory', 400),
					'.cardSlider__media': {
						transform: 'scale(1.22)',
					},
					'.cardSlider__overlay': {
						background: 'rgba(0, 0, 0, 0.2)',
					},
					'.cardSlider__content': {
						gap: 0,
					},
					'.cardSlider__cardSubtitle': {
						opacity: 0,
						maxHeight: 0,
						transform: 'translateY(-8px)',
					},
					'.cardSlider__cardIconRow': {
						opacity: 0,
						maxHeight: 0,
						transform: 'translateY(-8px)',
					},
					'.cardSlider__cardTitle': {
						fontSize: remCalc(39),
						lineHeight: remCalc(47),
						letterSpacing: remCalc(0.5),
					},
				},
			},
		},

		[theme.breakpoints.down('md')]: {
			'.cardSlider__navigation': {
				button: {
					width: remCalc(44),
					height: remCalc(44),
				},
			},

			'.cardSlider__content': {
				padding: remCalc(28),
			},

			'.cardSlider__cardTitle--variant2': {
				fontSize: remCalc(39),
				lineHeight: remCalc(47),
			},

			'.cardSlider__card--default': {
				'&:focus-within .cardSlider__cardTitle': {
					fontSize: remCalc(39),
					lineHeight: remCalc(47),
				},
				'@media (hover: hover) and (pointer: fine)': {
					'&:hover .cardSlider__cardTitle': {
						fontSize: remCalc(39),
						lineHeight: remCalc(47),
					},
				},
			},
		},

		'@media (prefers-reduced-motion: reduce)': {
			'.cardSlider__media, .cardSlider__overlay, .cardSlider__content, .cardSlider__cardSubtitle, .cardSlider__cardTitle': {
				transition: 'none',
			},
		},
	},
	'.cardSlider__navigation': {
		display: 'flex',
		alignItems: 'center',
		gap: remCalc(20),
		justifyContent: 'flex-end',
		[theme.breakpoints.down('sm')]: {
			justifyContent: 'flex-start',
		},
		button: {
			display: 'inline-flex',
			alignItems: 'center',
			border: 'none',
			justifyContent: 'center',
			background: 'transparent',
			backgroundColor: 'transparent',
			color: getColor('ivory', 600),
			transition: 'all .2s',

			svg: {
				width: remCalc(44),
				height: remCalc(38),
			},

			'&:focus-visible': {
				outline: `${remCalc(2)} solid ${getColor('blue', 400)}`,
				outlineOffset: remCalc(2),
			},

			'@media (hover: hover) and (pointer: fine)': {
				'&:hover': {
					cursor: 'pointer',
					background: getColor('ivory', 400),
				},
			},
		},
	},
};

export default styles;
